import { useCallback, useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useStores from 'shared/hooks/use-stores';
import { useRouter } from 'next/router';
import useUI from 'src/hooks/use-ui';
import useUser from 'src/hooks/use-user';
import { useCountdownTimer } from 'src/hooks/use-countdown-timer';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

type UseKioskCheckoutSuccessModalV2Params = {
  onClose: () => void;
};

type UseKioskCheckoutSuccessModalV2Return = {
  secondsLeft: number;
  handleOnClose: () => void;
  isAnonymousKioskCheckout: boolean;
  orderNumber: string | null;
  kioskInstructions: string;
  totalSeconds: number;
};

export function useKioskCheckoutSuccessModalV2({
  onClose,
}: UseKioskCheckoutSuccessModalV2Params): UseKioskCheckoutSuccessModalV2Return {
  const { Cart } = useStores();
  const { dispensary } = useDispensary();
  const User = useUser();
  const router = useRouter();
  const { dispensaryRootUrl } = useUI();
  const cName = dispensary?.cName ?? '';
  const isAnonymousKioskCheckout = Cart?.isAnonymousKioskCheckout ?? false;
  const kioskInstructions = dispensary?.orderTypesConfigV2?.kiosk?.kioskInstructions ?? '';
  const orderNumber = Cart?.anonymousOrderId ?? null;
  const isUserLoggedIn = useObserver(() => User?.isLoggedIn);
  const isKioskProEnabled = useKioskPro();

  const handleOnClose = useCallback((): void => {
    Cart.setAnonymousOrderId('');
    Cart.setIsAnonymousKioskCheckout(false);
    onClose();

    if (isKioskProEnabled) {
      Cart?.clearOrder();

      if (isUserLoggedIn) {
        User?.logout();
      }

      void router.push(
        `/${String(dispensaryRootUrl)}/[cName]/lock`,
        `/${String(dispensaryRootUrl)}/${String(cName)}/lock`
      );
    }
  }, [Cart, User, cName, dispensaryRootUrl, isKioskProEnabled, isUserLoggedIn, onClose, router]);

  const totalSeconds = 8;

  const { startTimer, secondsLeft } = useCountdownTimer({
    initialSeconds: totalSeconds,
    onTimerStop: handleOnClose,
  });

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  return {
    secondsLeft,
    totalSeconds,
    handleOnClose,
    isAnonymousKioskCheckout,
    orderNumber,
    kioskInstructions,
  };
}
